import { useState } from "react";

import { Badge, Button, type Column, EmptyState, Table } from "@/components/common";
import { Guard } from "@/auth/Guard";
import { PERM } from "@/auth/permissions";
import { usePermission } from "@/auth/usePermission";
import { useDeleteRole, useRoles } from "@/hooks/useRoles";
import { extractErrorMessage } from "@/services/axiosConfig";
import type { Role } from "@/types/role";
import { ConfirmDialog } from "./ConfirmDialog";
import { RoleFormModal } from "./RoleFormModal";

export function RolesTab({ workspaceId }: { workspaceId: number }) {
  const { data: roles = [], isLoading } = useRoles(workspaceId);
  const del = useDeleteRole(workspaceId);
  const canWrite = usePermission(PERM.rolesWrite);

  const [editing, setEditing] = useState<Role | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Role | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleteError(null);
    try {
      await del.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
    } catch (err) {
      setDeleteError(extractErrorMessage(err));
    }
  }

  const columns: Column<Role>[] = [
    {
      id: "name",
      header: "Papel",
      cell: (r) => (
        <div>
          <div className="razao">{r.name}</div>
          {r.description && <div className="fantasia">{r.description}</div>}
        </div>
      ),
    },
    {
      id: "type",
      header: "Tipo",
      cell: (r) =>
        r.is_system ? <Badge tone="subtle">Sistema</Badge> : <Badge tone="primary">Custom</Badge>,
    },
    {
      id: "perms",
      header: "Permissões",
      cell: (r) => <span className="td-small">{r.permission_codes.length}</span>,
    },
    {
      id: "actions",
      header: "",
      cell: (r) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button variant="ghost" iconLeft="settings" onClick={() => setEditing(r)}>
            {r.is_system || !canWrite ? "Ver" : "Editar"}
          </Button>
          {!r.is_system && canWrite && (
            <Button variant="ghost" iconLeft="x" onClick={() => setPendingDelete(r)}>
              Excluir
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Guard permission={PERM.rolesWrite}>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <Button variant="primary" iconLeft="plus" onClick={() => setCreating(true)}>
            Novo papel
          </Button>
        </div>
      </Guard>

      <Table
        rows={roles}
        columns={columns}
        getKey={(r) => String(r.id)}
        empty={
          <EmptyState
            icon="shield-check"
            title={isLoading ? "Carregando…" : "Nenhum papel"}
            description={isLoading ? undefined : "Crie um papel custom para este workspace."}
          />
        }
      />

      {creating && (
        <RoleFormModal workspaceId={workspaceId} role={null} onClose={() => setCreating(false)} />
      )}
      {editing && (
        <RoleFormModal workspaceId={workspaceId} role={editing} onClose={() => setEditing(null)} />
      )}
      {pendingDelete && (
        <ConfirmDialog
          title="Excluir papel"
          message={`Excluir o papel "${pendingDelete.name}"? Papéis em uso por membros não podem ser excluídos.`}
          confirmLabel="Excluir"
          loading={del.isPending}
          error={deleteError}
          onConfirm={() => void confirmDelete()}
          onClose={() => {
            setPendingDelete(null);
            setDeleteError(null);
          }}
        />
      )}
    </>
  );
}
